// Cadastro de um agrupamento de faturamento (#5781). Criar e editar usam o mesmo formulário.
// A validação é a mesma regra pura do banco (src/lib/agrupamentoFaturamento.ts), para o erro
// aparecer antes de gravar em vez de voltar como CHECK do Postgres.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2 } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import {
  DIA_VENCIMENTO_PADRAO,
  normalizarCnpjPagador,
  normalizarListaCodigos,
  validarAgrupamento,
} from '../../src/lib/agrupamentoFaturamento';
import type { AgrupamentoFaturamento } from '../../types';
import { CONTAS_SICOOB, isContaSicoob, NOME_CONTA_SICOOB } from '../../src/lib/contaSicoob';
import { useContasSicoob } from '../../src/hooks/useContasSicoob';

const CAMPO =
  'w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-primary dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100';
const ROTULO = 'block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1';

interface Props {
  aberto: boolean;
  agrupamento: AgrupamentoFaturamento | null;
  onFechar: () => void;
  onSalvo: (msg: string) => void;
}

const vazio = {
  nome: '', cnpj_pagador: '', cod_agrupamento_concessionaria: '', concessionaria: 'Equatorial',
  dia_vencimento: String(DIA_VENCIMENTO_PADRAO), pagador_nome: '', pagador_logradouro: '',
  pagador_cidade: '', pagador_uf: '', pagador_cep: '', observacoes: '',
  // Régua própria (#5787). Vazio = padrão do contrato.
  dia_limite_emissao: '', dia_auditoria_util: '', dia_malote_util: '', dias_pagamento_edital: '',
  // Boleto agrupado (#5785): conta de quem emite e % retido na fonte (0 = boleto cheio).
  conta_sicoob: '', retencao_percentual: '0',
};

/** Campo numérico opcional: vazio vira null (usa o padrão), texto inválido também. */
const numeroOuNulo = (v: string) => {
  const n = Number(String(v).trim());
  return String(v).trim() === '' || !Number.isFinite(n) ? null : Math.trunc(n);
};

/** % com vírgula ou ponto. Fora de 0 a 99,99 devolve null (a validação avisa antes de gravar). */
const percentualRetencao = (v: string): number | null => {
  const t = String(v).trim().replace(',', '.');
  if (t === '') return 0;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 && n < 100 ? Math.round(n * 100) / 100 : null;
};

const AgrupamentoFaturamentoModal: React.FC<Props> = ({ aberto, agrupamento, onFechar, onSalvo }) => {
  const [form, setForm] = useState(vazio);
  const [salvando, setSalvando] = useState(false);
  const [erroBanco, setErroBanco] = useState<string | null>(null);
  const [tentouSalvar, setTentouSalvar] = useState(false);
  const tituloId = React.useId();
  const primeiroCampo = useRef<HTMLInputElement>(null);
  const contasSicoob = useContasSicoob(aberto);

  useEffect(() => {
    if (!aberto) return;
    setErroBanco(null);
    setTentouSalvar(false);
    setForm(agrupamento
      ? {
          nome: agrupamento.nome,
          cnpj_pagador: agrupamento.cnpj_pagador,
          // Vários códigos (a SAP tem dois). Vem da tabela de códigos; o campo antigo é só reserva.
          cod_agrupamento_concessionaria: (agrupamento.codigos?.length
            ? agrupamento.codigos
            : [agrupamento.cod_agrupamento_concessionaria ?? '']).filter(Boolean).join(', '),
          concessionaria: agrupamento.concessionaria,
          dia_vencimento: String(agrupamento.dia_vencimento),
          pagador_nome: agrupamento.pagador_nome ?? '',
          pagador_logradouro: agrupamento.pagador_logradouro ?? '',
          pagador_cidade: agrupamento.pagador_cidade ?? '',
          pagador_uf: agrupamento.pagador_uf ?? '',
          pagador_cep: agrupamento.pagador_cep ?? '',
          observacoes: agrupamento.observacoes ?? '',
          dia_limite_emissao: agrupamento.dia_limite_emissao != null ? String(agrupamento.dia_limite_emissao) : '',
          dia_auditoria_util: agrupamento.dia_auditoria_util != null ? String(agrupamento.dia_auditoria_util) : '',
          dia_malote_util: agrupamento.dia_malote_util != null ? String(agrupamento.dia_malote_util) : '',
          dias_pagamento_edital: agrupamento.dias_pagamento_edital != null ? String(agrupamento.dias_pagamento_edital) : '',
          conta_sicoob: agrupamento.conta_sicoob ?? '',
          retencao_percentual: agrupamento.retencao_percentual != null ? String(agrupamento.retencao_percentual) : '0',
        }
      : vazio);
    setTimeout(() => primeiroCampo.current?.focus(), 0);
  }, [aberto, agrupamento]);

  // Esc fecha, como nos outros modais do módulo.
  useEffect(() => {
    if (!aberto) return;
    const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape' && !salvando) onFechar(); };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [aberto, salvando, onFechar]);

  const erros = useMemo(() => {
    const lista = validarAgrupamento({
      nome: form.nome,
      cnpj_pagador: form.cnpj_pagador,
      dia_vencimento: Number(form.dia_vencimento),
      pagador_uf: form.pagador_uf,
      pagador_cep: form.pagador_cep,
    });
    // Valor inválido não pode virar 0 calado: o boleto sairia cheio sem ninguém perceber.
    return percentualRetencao(form.retencao_percentual) === null
      ? [...lista, 'Retenção na fonte deve ser um número de 0 a 99,99.']
      : lista;
  }, [form]);

  if (!aberto) return null;

  /** Troca o conjunto de códigos do agrupamento de uma vez (a função do banco é atômica). */
  const gravarCodigos = async (agrupamentoId: number) => {
    const { data, error } = await supabase.rpc('definir_codigos_agrupamento', {
      p_agrupamento_id: agrupamentoId,
      p_codigos: normalizarListaCodigos(form.cod_agrupamento_concessionaria),
      p_concessionaria: form.concessionaria.trim() || 'Equatorial',
    });
    if (error) throw error;
    if ((data as any)?.ok !== true) throw new Error('O banco não confirmou os códigos.');
  };

  const salvar = async () => {
    setTentouSalvar(true);
    if (erros.length > 0) return;
    setSalvando(true);
    setErroBanco(null);
    try {
      const dados = {
        nome: form.nome.trim(),
        cnpj_pagador: normalizarCnpjPagador(form.cnpj_pagador),
        concessionaria: form.concessionaria.trim() || 'Equatorial',
        dia_vencimento: Number(form.dia_vencimento),
        pagador_nome: form.pagador_nome.trim() || null,
        pagador_logradouro: form.pagador_logradouro.trim() || null,
        pagador_cidade: form.pagador_cidade.trim() || null,
        pagador_uf: form.pagador_uf.trim().toUpperCase() || null,
        pagador_cep: form.pagador_cep.replace(/\D/g, '') || null,
        observacoes: form.observacoes.trim() || null,
        dia_limite_emissao: numeroOuNulo(form.dia_limite_emissao),
        dia_auditoria_util: numeroOuNulo(form.dia_auditoria_util),
        dia_malote_util: numeroOuNulo(form.dia_malote_util),
        dias_pagamento_edital: numeroOuNulo(form.dias_pagamento_edital),
        conta_sicoob: isContaSicoob(form.conta_sicoob) ? form.conta_sicoob : null,
        retencao_percentual: percentualRetencao(form.retencao_percentual) ?? 0,
      };

      if (agrupamento) {
        // Só grava se ninguém mexeu desde que a tela abriu: sem isso, o segundo a salvar
        // apaga a alteração do primeiro sem ninguém perceber.
        const { data, error } = await supabase
          .from('agrupamentos_faturamento')
          .update(dados)
          .eq('id', agrupamento.id)
          .eq('atualizado_em', agrupamento.atualizado_em)
          .select('id');
        if (error) throw error;
        if (!data || data.length === 0) {
          setErroBanco('Outra pessoa alterou este agrupamento enquanto você editava. Feche, atualize a tela e refaça a alteração.');
          return;
        }
        try {
          await gravarCodigos(agrupamento.id);
        } catch (e: any) {
          // Os dados já mudaram (e o atualizado_em junto): salvar de novo daqui cairia no aviso de
          // "outra pessoa alterou". Recarregar a tela antes de refazer os códigos.
          onSalvo('Agrupamento atualizado, mas os códigos da concessionária não foram gravados.');
          setErroBanco(`Dados salvos, mas os códigos não: ${(e?.message || 'erro desconhecido').replace(/\.$/, '')}. Feche, atualize a tela e salve os códigos de novo.`);
          return;
        }
        onSalvo('Agrupamento atualizado.');
      } else {
        const { data, error } = await supabase.from('agrupamentos_faturamento').insert(dados).select('id').single();
        if (error) throw error;
        try {
          await gravarCodigos((data as { id: number }).id);
        } catch (e: any) {
          // O agrupamento já existe: dizer isso, senão o operador tenta criar de novo e duplica.
          onSalvo('Agrupamento criado, mas os códigos da concessionária não foram gravados.');
          setErroBanco(`Agrupamento criado, mas os códigos não: ${(e?.message || 'erro desconhecido').replace(/\.$/, '')}. Abra "Editar" e salve os códigos de novo.`);
          return;
        }
        onSalvo('Agrupamento criado.');
      }
      onFechar();
    } catch (e: any) {
      const cod = e?.code;
      setErroBanco(
        cod === '23505' ? (e?.message || 'Esse código já pertence a outro agrupamento.')
        : cod === '42501' ? 'Seu usuário não tem permissão para gravar agrupamentos.'
        : e?.message || 'Não foi possível salvar.',
      );
    } finally {
      setSalvando(false);
    }
  };

  const campo = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby={tituloId}>
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-xl dark:bg-gray-800">
        <header className="flex items-center justify-between border-b border-gray-100 px-5 py-4 dark:border-gray-700">
          <h2 id={tituloId} className="text-base font-bold text-gray-900 dark:text-white">
            {agrupamento ? 'Editar agrupamento' : 'Novo agrupamento de faturamento'}
          </h2>
          <button type="button" onClick={onFechar} aria-label="Fechar" className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700">
            <X size={18} />
          </button>
        </header>

        <div className="space-y-4 p-5">
          <div>
            <label className={ROTULO} htmlFor="ag-nome">Nome *</label>
            <input id="ag-nome" ref={primeiroCampo} value={form.nome} onChange={campo('nome')} className={CAMPO} placeholder="Secretaria de Saúde" />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={ROTULO} htmlFor="ag-cnpj">CNPJ do pagador *</label>
              <input id="ag-cnpj" value={form.cnpj_pagador} onChange={campo('cnpj_pagador')} className={CAMPO} placeholder="01.789.551/0001-49" inputMode="numeric" />
            </div>
            <div>
              <label className={ROTULO} htmlFor="ag-dia">Dia de vencimento *</label>
              <input id="ag-dia" value={form.dia_vencimento} onChange={campo('dia_vencimento')} className={CAMPO} inputMode="numeric" />
              <p className="mt-1 text-[11px] text-gray-400">De 1 a 28, para existir em todo mês.</p>
            </div>
            <div>
              <label className={ROTULO} htmlFor="ag-concessionaria">Concessionária</label>
              <input id="ag-concessionaria" value={form.concessionaria} onChange={campo('concessionaria')} className={CAMPO} />
            </div>
            <div>
              <label className={ROTULO} htmlFor="ag-cod">Códigos do agrupamento na concessionária</label>
              <input id="ag-cod" value={form.cod_agrupamento_concessionaria} onChange={campo('cod_agrupamento_concessionaria')} className={CAMPO} placeholder="0044000, 0066944" />
              <p className="mt-1 text-[11px] text-gray-400">
                Separe por vírgula quando a Secretaria responder por mais de um (ex.: SAP). Mantém os zeros à esquerda.
              </p>
            </div>
            <div>
              <label className={ROTULO} htmlFor="ag-conta">Conta Sicoob do boleto</label>
              <select id="ag-conta" value={form.conta_sicoob} onChange={e => setForm(f => ({ ...f, conta_sicoob: e.target.value }))} className={CAMPO}>
                <option value="">Ainda não definida</option>
                {CONTAS_SICOOB.map(conta => {
                  // Conta ainda não liberada aparece, mas não pode ser escolhida. A que já
                  // está gravada continua selecionável para não sumir do campo.
                  const liberada = contasSicoob?.find(c => c.conta === conta)?.emissao_ativa ?? true;
                  return (
                    <option key={conta} value={conta} disabled={!liberada && form.conta_sicoob !== conta}>
                      {NOME_CONTA_SICOOB[conta]}{liberada ? '' : ' (ainda não liberada)'}
                    </option>
                  );
                })}
              </select>
              <p className="mt-1 text-[11px] text-gray-400">Sem conta definida, o boleto do agrupamento não é emitido.</p>
            </div>
            <div>
              <label className={ROTULO} htmlFor="ag-retencao">Retenção na fonte (%)</label>
              <input id="ag-retencao" value={form.retencao_percentual} onChange={campo('retencao_percentual')} className={CAMPO} inputMode="decimal" placeholder="0" />
              <p className="mt-1 text-[11px] text-gray-400">0 = boleto com o valor cheio. Descontado do total na emissão.</p>
            </div>
          </div>

          <details className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
            <summary className="cursor-pointer text-sm font-semibold text-gray-700 dark:text-gray-200">
              Dados do pagador para o boleto (opcional)
            </summary>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={ROTULO} htmlFor="ag-pag-nome">Nome do pagador</label>
                <input id="ag-pag-nome" value={form.pagador_nome} onChange={campo('pagador_nome')} className={CAMPO} />
              </div>
              <div className="sm:col-span-2">
                <label className={ROTULO} htmlFor="ag-pag-log">Endereço</label>
                <input id="ag-pag-log" value={form.pagador_logradouro} onChange={campo('pagador_logradouro')} className={CAMPO} />
              </div>
              <div>
                <label className={ROTULO} htmlFor="ag-pag-cidade">Cidade</label>
                <input id="ag-pag-cidade" value={form.pagador_cidade} onChange={campo('pagador_cidade')} className={CAMPO} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={ROTULO} htmlFor="ag-pag-uf">UF</label>
                  <input id="ag-pag-uf" value={form.pagador_uf} onChange={campo('pagador_uf')} className={CAMPO} maxLength={2} />
                </div>
                <div>
                  <label className={ROTULO} htmlFor="ag-pag-cep">CEP</label>
                  <input id="ag-pag-cep" value={form.pagador_cep} onChange={campo('pagador_cep')} className={CAMPO} inputMode="numeric" />
                </div>
              </div>
            </div>
          </details>

          <details className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
            <summary className="cursor-pointer text-sm font-semibold text-gray-700 dark:text-gray-200">
              Régua de prazos própria (opcional)
            </summary>
            <p className="mt-2 text-[11px] text-gray-400">
              Em branco = padrão do contrato: emitir até o último dia útil, auditar no 1º dia útil,
              malote até o 5º dia útil e 30 dias para pagar.
            </p>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <div>
                <label className={ROTULO} htmlFor="ag-lim">Emitir até o dia</label>
                <input id="ag-lim" value={form.dia_limite_emissao} onChange={campo('dia_limite_emissao')} className={CAMPO} inputMode="numeric" placeholder="último dia útil" />
              </div>
              <div>
                <label className={ROTULO} htmlFor="ag-aud">Auditoria no Nº dia útil</label>
                <input id="ag-aud" value={form.dia_auditoria_util} onChange={campo('dia_auditoria_util')} className={CAMPO} inputMode="numeric" placeholder="1" />
              </div>
              <div>
                <label className={ROTULO} htmlFor="ag-mal">Malote no Nº dia útil</label>
                <input id="ag-mal" value={form.dia_malote_util} onChange={campo('dia_malote_util')} className={CAMPO} inputMode="numeric" placeholder="5" />
              </div>
              <div>
                <label className={ROTULO} htmlFor="ag-edital">Dias para pagar após o malote</label>
                <input id="ag-edital" value={form.dias_pagamento_edital} onChange={campo('dias_pagamento_edital')} className={CAMPO} inputMode="numeric" placeholder="30" />
              </div>
            </div>
          </details>

          <div>
            <label className={ROTULO} htmlFor="ag-obs">Observações</label>
            <textarea id="ag-obs" value={form.observacoes} onChange={campo('observacoes')} className={`${CAMPO} min-h-[70px]`} />
          </div>

          {tentouSalvar && erros.length > 0 && (
            <ul className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300" role="alert">
              {erros.map(e => <li key={e}>• {e}</li>)}
            </ul>
          )}
          {erroBanco && (
            <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300" role="alert">
              {erroBanco}
            </p>
          )}
        </div>

        <footer className="flex justify-end gap-2 border-t border-gray-100 px-5 py-4 dark:border-gray-700">
          <button type="button" onClick={onFechar} disabled={salvando} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-60 dark:border-gray-600 dark:text-gray-200">
            Cancelar
          </button>
          <button type="button" onClick={salvar} disabled={salvando} aria-busy={salvando} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-60">
            {salvando && <Loader2 size={15} className="animate-spin" />}
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export default AgrupamentoFaturamentoModal;
